import { register } from './index';
import {
  destroyCurseFromHand, grantGold, grantXp, pickRandomInstanceIds, queueOptionalCurseSelfDestroy,
} from '../effectHelpers';
import { getPlayerById, computeRemainingGold } from '../../state/selectors';

const CURSE_OF_DISCORD_COST = 2;

function updatePlayer(state, playerId, updateFn) {
  return { ...state, players: state.players.map((p) => (p.id === playerId ? updateFn(p) : p)) };
}

// "If this is the first ability you have used this turn, destroy this card.
// You cannot use any more abilities this turn." - checked via
// usedAbilityInstanceIds being empty at the moment this resolves (this
// ability's own usage marker is added by the caller *after* resolve()
// returns, so an empty array here means truly nothing has been used yet).
function decayResolve(state, { playerId, instanceId }) {
  const player = getPlayerById(state, playerId);
  if (player.turnFlags.usedAbilityInstanceIds.length > 0) {
    throw new Error('Curse of Decay can only be destroyed as the first ability used this turn.');
  }
  const destroyed = destroyCurseFromHand(state, playerId, instanceId);
  return updatePlayer(destroyed, playerId, (p) => ({
    ...p, turnFlags: { ...p.turnFlags, allAbilitiesLocked: true },
  }));
}

register('curse-decay', {
  village: [{ resolve: decayResolve }],
  dungeon: [{ resolve: decayResolve }],
});

// Brian reported this directly: "I spent all 7 of my gold on a card but
// was then still able to use the village ability on a curse to lose 2
// gold to destroy the curse even though I was out of gold." The printed
// text ("Lose 2 gold to destroy this curse") is a COST, not a flat
// after-the-fact penalty - grantGold's own -2 call had nothing gating it,
// so it always succeeded regardless of what the player could actually
// afford, unlike a normal buy (villageActions.js's performBuy, which
// throws via this exact same computeRemainingGold check before spending
// anything). No other card in the registry calls grantGold with a
// negative amount, so this was the only place missing the guard.
register('curse-discord', {
  village: [{
    resolve: (state, { playerId, instanceId }) => {
      const goldAvailable = computeRemainingGold(state, playerId);
      if (goldAvailable < CURSE_OF_DISCORD_COST) {
        throw new Error(`Not enough gold (need ${CURSE_OF_DISCORD_COST}, have ${goldAvailable}).`);
      }
      const afterCurse = destroyCurseFromHand(state, playerId, instanceId);
      return grantGold(afterCurse, playerId, -CURSE_OF_DISCORD_COST);
    },
    // Same computeRemainingGold gate as Blood Debt's own isUsable
    // (villages.js) - hides/disables the button proactively instead of
    // only rejecting the dispatch after the fact, and also keeps the
    // "unused abilities - end turn anyway?" warning from firing when this
    // simply can't be afforded (same bug Blood Debt's own comment cites).
    isUsable: (state, { playerId }) => computeRemainingGold(state, playerId) >= CURSE_OF_DISCORD_COST,
  }],
});

register('curse-horror', {
  dungeon: [{
    resolve: (state, { playerId, instanceId }) => {
      const destroyed = destroyCurseFromHand(state, playerId, instanceId);
      return updatePlayer(destroyed, playerId, (p) => ({
        ...p,
        turnFlags: {
          ...p.turnFlags,
          lightForcedToZero: true,
          cannotEquipWeapons: true,
          dungeonAbilitiesLocked: true,
        },
      }));
    },
  }],
});

const hostilityIsUsable = (state, { playerId }) => getPlayerById(state, playerId).xp >= 2;

register('curse-hostility', {
  village: [{
    resolve: (state, { playerId, instanceId }) => {
      const player = getPlayerById(state, playerId);
      if (player.xp < 2) throw new Error('Not enough XP to destroy this curse.');
      return grantXp(destroyCurseFromHand(state, playerId, instanceId), playerId, -2);
    },
    isUsable: hostilityIsUsable,
  }],
  dungeon: [{
    resolve: (state, { playerId, instanceId }) => {
      const player = getPlayerById(state, playerId);
      if (player.xp < 2) throw new Error('Not enough XP to destroy this curse.');
      return grantXp(destroyCurseFromHand(state, playerId, instanceId), playerId, -2);
    },
    isUsable: hostilityIsUsable,
  }],
});

function neglectResolve(state, { playerId, instanceId, choices }) {
  const player = getPlayerById(state, playerId);
  const otherHandCards = player.hand.filter((id) => id !== instanceId);
  const toDiscard = (choices?.discardInstanceIds && choices.discardInstanceIds.length === 2)
    ? choices.discardInstanceIds
    : otherHandCards.slice(0, 2);
  if (toDiscard.length < 2) throw new Error('Need 2 other cards in hand to discard.');
  let next = destroyCurseFromHand(state, playerId, instanceId);
  for (const id of toDiscard) {
    next = updatePlayer(next, playerId, (p) => ({
      ...p,
      hand: p.hand.filter((h) => h !== id),
      discard: [...p.discard, id],
    }));
  }
  return next;
}

const neglectIsUsable = (state, { playerId, instanceId }) => getPlayerById(state, playerId)
  .hand.filter((id) => id !== instanceId).length >= 2;

register('curse-neglect', {
  village: [{ resolve: neglectResolve, isUsable: neglectIsUsable }],
  dungeon: [{ resolve: neglectResolve, isUsable: neglectIsUsable }],
});

function shameResolve(state, { playerId, instanceId }) {
  const destroyed = destroyCurseFromHand(state, playerId, instanceId);
  return updatePlayer(destroyed, playerId, (p) => ({
    ...p, turnFlags: { ...p.turnFlags, handSizeReduction: p.turnFlags.handSizeReduction + 2 },
  }));
}

register('curse-shame', {
  village: [{ resolve: shameResolve }],
  dungeon: [{ resolve: shameResolve }],
});

register('curse-sloth', {
  dungeon: [{
    resolve: (state, { playerId, instanceId }) => {
      const destroyed = destroyCurseFromHand(state, playerId, instanceId);
      return updatePlayer(destroyed, playerId, (p) => ({
        ...p,
        turnFlags: {
          ...p.turnFlags,
          bonusTotalAttack: p.turnFlags.bonusTotalAttack - 3,
          cannotEquipWeapons: true,
          dungeonAbilitiesLocked: true,
        },
      }));
    },
  }],
});

// Caverns of Bane, Curse of War: "You cannot rest or prepare." (enforced in
// actions/index.js's assertMayRestOrPrepare, a hand-presence lockout - not
// this handler's job, same "gate the action, not the ability" shape as
// Charm of Venery). "Trophy: Attack -1" (attacks field, same as every other
// curse). This ability line is the "Select 2 random cards from your hand
// (excluding this card). You may destroy 1 of them to destroy this card."
// - randomly picks 2 candidates right now (so the player can actually see
// what got revealed before deciding), then queues an opt-in/decline choice
// rather than resolving immediately, since destroying is optional and
// mid-resolution.
function warResolve(state, { playerId, instanceId }) {
  const player = getPlayerById(state, playerId);
  const candidates = player.hand.filter((id) => id !== instanceId);
  const { state: afterPick, instanceIds: picked } = pickRandomInstanceIds(state, candidates, 2);
  return queueOptionalCurseSelfDestroy(afterPick, {
    playerId,
    selfInstanceId: instanceId,
    candidateInstanceIds: picked,
    reason: 'Curse of War: destroy 1 of these 2 randomly-selected cards to destroy this curse.',
  });
}

register('curse-war', {
  village: [{ resolve: warResolve }],
  dungeon: [{ resolve: warResolve }],
});
